import { useState, type JSX, type ReactNode } from 'react'
import type { CashPositionMonth } from '../api'
import type { CashMode } from '../cashMode'
import { compareYen, monthLabel, yen } from '../format'
import { CHART_INK, INFLOW_RAMP, SERIES, hatchSwatch } from '../charts/palette'
import { LIVE_NOTE, pointNote, toCashPoints, type CashPoint } from '../charts/cashPoints'
import { compactYen, domainFor, monthLabelY, plotValue, ticksFor, yFor } from '../charts/scale'
import { NoDataZone } from '../charts/NoDataZone'
import { monthsAfter } from '../timeWindow'
import { PipelineToggle } from './PlanToggle'
import { SourceNote } from './FreshnessStatus'

// The "Month-end actuals" reading of the Cash position chart (GTM-54), the
// reading in use (its switch is retired, src/cashMode.ts). The single-anchor reading lives in CashPositionChart, untouched.
//
// PAST MONTHS ARE RECORDS. Each ended month plots the real month-end bank
// balance: the audited figure where one was entered, otherwise freee's balance
// computed from bank transactions. A solid line runs through those.
//
// A MISSING MONTH IS A GAP. A month that ended with no balance known breaks the
// line (no interpolation, no zero) and is marked with a cross on the baseline.
//
// THE PROJECTION STARTS FROM THE LATEST ACTUAL. A dashed stretch begins AT the
// latest actual point, so it is continuous, and carries on over a shaded
// ground. The weighted pipeline is a layer there only (a second thin line and
// the same hatched band as the bars): in the past the balance is what it is.
// The payroll-reporting boundary (projectedFrom) plays no part in this view.
//
// THE LIVE BALANCE IS ITS OWN MARKER on the current month, a snapshot that is
// never joined to the line, because it is not a month-end.

const W = 720
const H = 274
const PAD = { top: 18, right: 16, bottom: 48, left: 62 }
const PLOT_W = W - PAD.left - PAD.right
const PLOT_H = H - PAD.top - PAD.bottom
const LABEL_Y = monthLabelY(PAD.top + PLOT_H)
const SURFACE = '#272C47'

export const MONTH_END_SWITCH_LABEL = 'Month-end actuals'

/**
 * The Cash position card, shared by both readings. The switches are flipped back
 * and forth to compare the two, so nothing they change sits above the plot: the
 * title line (title, source note, switches) is the same in every state, the plot
 * (or the empty notice) comes straight after it, and the description and legend,
 * which do change, sit underneath.
 */
export function CashPositionFrame({
  sourceNote,
  switches,
  notes,
  children,
}: {
  sourceNote?: string | null
  switches: ReactNode
  /** Description and legend, drawn under the plot. */
  notes?: ReactNode
  children: ReactNode
}): JSX.Element {
  return (
    <section className="panel chart-panel">
      <header className="chart-title-row">
        <div className="chart-title">
          <h2>Cash position</h2>
          <SourceNote text={sourceNote ?? null} />
        </div>
        <div className="chart-switches">{switches}</div>
      </header>
      {children}
      {notes ? <div className="chart-notes">{notes}</div> : null}
    </section>
  )
}

/** The switch that moves the card between the two readings. */
export function MonthEndSwitch({
  mode,
  onChange,
}: {
  mode: CashMode
  onChange: (next: CashMode) => void
}): JSX.Element {
  const on = mode === 'month-end-actuals'
  return (
    <div className="plan-control">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        className={`plan-switch${on ? ' on' : ''}`}
        onClick={() => onChange(on ? 'single-anchor' : 'month-end-actuals')}
        title="Preview: plot each past month's real month-end bank balance and project forward from the latest one. Also applies to the scenario planner below. Your choice is remembered on this device."
      >
        <span className="switch-track" aria-hidden="true">
          <span className="switch-thumb" />
        </span>
        {MONTH_END_SWITCH_LABEL}
        <span className="preview-tag">preview</span>
      </button>
    </div>
  )
}

export function CashPositionMonthEnd({
  months,
  showPipeline,
  onShowPipelineChange,
  liveBalance,
  sourceNote,
  windowEnd,
  modeToggle,
}: {
  months: CashPositionMonth[]
  showPipeline: boolean
  onShowPipelineChange: (next: boolean) => void
  liveBalance: { month: string; balance: string } | null
  sourceNote?: string | null
  windowEnd: string
  modeToggle: JSX.Element | null
}): JSX.Element {
  const [hover, setHover] = useState<number | null>(null)
  const points = toCashPoints(months)
  const lastActual = points.reduce((found, p, i) => (p.kind === 'actual' ? i : found), -1)

  // No actual inside the window is fine when a projection is: the window can
  // start after the latest actual (e.g. Rolling 18), and the projection is still
  // anchored on it. The notice is only for "no actual anywhere, nothing projected".
  const anyProjected = points.some((p) => p.kind === 'projected' && p.booked !== null)
  if (lastActual < 0 && !anyProjected) {
    // Same title line as a drawn chart, so the switch is right where it was
    // when the reader flips back.
    return (
      <CashPositionFrame
        sourceNote={sourceNote}
        switches={
          <>
            <PipelineToggle
              on={showPipeline}
              onChange={onShowPipelineChange}
              affected={0}
              total={0}
            />
            {modeToggle}
          </>
        }
      >
        <p className="notice">
          No month-end bank balance is on record for these months, so there is nothing to plot and
          nothing to project from. It appears once a month with a bank balance has closed; it is
          never drawn off a guessed figure.
        </p>
      </CashPositionFrame>
    )
  }

  const n = points.length
  const projectionAnchor =
    lastActual >= 0
      ? (months[lastActual]?.projectionAnchorMonth ?? points[lastActual]?.month ?? null)
      : (months.find((m) => m.pointKind === 'projected')?.projectionAnchorMonth ?? null)
  const anchorValue = points[lastActual]?.booked ?? 0

  // Solid runs of consecutive actual months. A missing month ends a run.
  const runs: number[][] = []
  points.forEach((p, i) => {
    if (p.kind !== 'actual') return
    const run = runs[runs.length - 1]
    if (run && run[run.length - 1] === i - 1) run.push(i)
    else runs.push([i])
  })

  // The dashed stretch: the latest actual, then every later projected month with
  // a value. Projected months with no value (no actual existed to project from)
  // simply have no vertex.
  const projectedIdx = points
    .map((p, i) => (i > lastActual && p.kind === 'projected' && p.booked !== null ? i : -1))
    .filter((i) => i >= 0)
  const pipelineIdx = points
    .map((p, i) => (i > lastActual && p.kind === 'projected' && p.withPipeline !== null ? i : -1))
    .filter((i) => i >= 0)
  const hasProjection = projectedIdx.length > 0

  const pipelineCount = months.filter(
    (m, i) =>
      points[i]?.kind === 'projected' &&
      points[i]?.booked !== null &&
      points[i]?.withPipeline !== null &&
      compareYen(m.projectedBalance ?? '0', m.projectedBalanceExPipeline ?? '0') !== 0,
  ).length
  const drawPipeline = showPipeline && pipelineCount > 0

  const liveIdx = liveBalance ? points.findIndex((p) => p.month === liveBalance.month) : -1
  const liveValue = liveBalance ? plotValue(liveBalance.balance) : null
  const drawLive = liveIdx >= 0 && liveValue !== null
  const missingIdx = points.map((p, i) => (p.kind === 'missing' ? i : -1)).filter((i) => i >= 0)

  const { min, max } = domainFor([
    ...points.flatMap((p) => [p.booked, p.withPipeline]).filter((v): v is number => v !== null),
    ...(drawLive && liveValue !== null ? [liveValue] : []),
  ])
  const lastMonth = points[n - 1]?.month ?? windowEnd
  const gap = monthsAfter(lastMonth, windowEnd)
  const band = PLOT_W / (n + gap.length)
  const y = (value: number): number => yFor(value, min, max, PAD.top, PLOT_H)
  const zero = y(0)
  const centre = (index: number): number => PAD.left + band * index + band / 2
  const bookedAt = (index: number): number => points[index]?.booked ?? 0
  const pipelineAt = (index: number): number => points[index]?.withPipeline ?? 0
  const pointsOf = (indices: number[], at: (index: number) => number): string =>
    indices.map((index) => `${centre(index)},${y(at(index))}`).join(' ')
  const baselineY = PAD.top + PLOT_H - 9

  // The projection is joined to the latest actual only when its first month
  // directly follows it. A missing month in between is never interpolated
  // across: the dashed line then starts on its own at the first projected month.
  const joined = (idx: number[]): number[] =>
    lastActual >= 0 && idx[0] === lastActual + 1 ? [lastActual, ...idx] : idx
  const stretch = joined(projectedIdx)
  const pipelineStretch = joined(pipelineIdx)
  const stretchPoints = (indices: number[], at: (index: number) => number): string =>
    indices.map((index) => `${centre(index)},${y(index === lastActual ? anchorValue : at(index))}`).join(' ')
  const boundaryX = hasProjection ? PAD.left + band * (lastActual + 1) : null

  const latest = projectedIdx.length > 0 ? months[projectedIdx[projectedIdx.length - 1] as number] : undefined
  const latestActual = months[lastActual]
  const active = hover === null ? null : (points[hover] ?? null)
  const activeMonth = hover === null ? null : (months[hover] ?? null)
  const hoveredGap = hover === null ? null : (gap[hover - n] ?? null)

  const valueFor = (m: CashPositionMonth, p: CashPoint): string | null =>
    p.kind === 'actual' ? m.actualBalance : p.kind === 'projected' ? m.projectedBalanceExPipeline : null

  const switches = (
    <>
      <PipelineToggle
        on={showPipeline}
        onChange={onShowPipelineChange}
        affected={pipelineCount}
        total={projectedIdx.length}
      />
      {modeToggle}
    </>
  )

  const notes = (
    <>
      <p className="chart-sub">
        Past months show the real bank balance at each month-end: freee&apos;s balance from
        bank transactions, or the audited figure where one was entered.
        {projectionAnchor && hasProjection && latest ? (
          <>
            {' '}The dashed line projects forward from the {monthLabel(projectionAnchor)} actual
            {latestActual?.actualBalance ? <> ({yen(latestActual.actualBalance)})</> : null} using booked AR, closed fundraising,
            payroll and planned costs: {yen(latest.projectedBalanceExPipeline ?? '0')} by{' '}
            {monthLabel(latest.month)}
            {drawPipeline ? (
              <>
                , or {yen(latest.projectedBalance ?? '0')} if the weighted pipeline (deals not
                yet signed, the hatched band) comes in
              </>
            ) : null}
            .
          </>
        ) : null}
        {missingIdx.length > 0 ? (
          <>
            {' '}A cross marks an ended month with no bank balance on record; the line breaks
            there rather than guessing.
          </>
        ) : null}
        {liveBalance && drawLive ? (
          <>
            {' '}freee&apos;s live bank balance for {monthLabel(liveBalance.month)} so far is{' '}
            {yen(liveBalance.balance)}; it is a snapshot, not a month-end, so it is not joined to
            the line.
          </>
        ) : null}
      </p>
      <ul className="legend">
        <li>
          <span className="swatch line" style={{ background: SERIES.blue }} /> Actual (month-end
          bank balance)
        </li>
        {drawPipeline && (
          <li>
            <span className="swatch" style={{ background: hatchSwatch(INFLOW_RAMP.ar) }} />{' '}
            Pipeline (weighted)
          </li>
        )}
        {hasProjection && (
          <li>
            <span className="swatch line dashed" /> Projected
          </li>
        )}
        {missingIdx.length > 0 && (
          <li>
            <span className="swatch missing" aria-hidden="true" /> No bank balance
          </li>
        )}
        {drawLive && (
          <li>
            <span className="swatch anchor" /> Live balance today
          </li>
        )}
      </ul>
    </>
  )

  return (
    <CashPositionFrame sourceNote={sourceNote} switches={switches} notes={notes}>
      <div className="chart-wrap">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="chart"
          role="img"
          aria-label={
            'Cash balance by month. Past months are the real month-end bank balance, drawn as a solid line; a cross marks an ended month with no bank balance on record. The dashed stretch is projected forward from the latest actual' +
            (drawPipeline ? ', with a second line adding weighted pipeline' : '') +
            (drawLive ? '. A separate marker shows the live bank balance today, which is not a month-end' : '') +
            '. The monthly net figures behind it are in the cash flow table below.'
          }
          onMouseLeave={() => setHover(null)}
        >
          <defs>
            <linearGradient id="cashAreaME" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={SERIES.blue} stopOpacity={0.32} />
              <stop offset="100%" stopColor={SERIES.blue} stopOpacity={0.02} />
            </linearGradient>
            <pattern
              id="pipelineBandME"
              width={6}
              height={6}
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect width={6} height={6} fill={INFLOW_RAMP.ar} opacity={0.12} />
              <line x1={0} y1={0} x2={0} y2={6} stroke={INFLOW_RAMP.ar} strokeWidth={1.5} opacity={0.7} />
            </pattern>
          </defs>

          {boundaryX !== null && (
            <g>
              <rect
                x={boundaryX}
                y={PAD.top}
                width={Math.max(0, PAD.left + band * n - boundaryX)}
                height={PLOT_H}
                fill="rgba(226, 232, 240, 0.045)"
              />
              <line
                x1={boundaryX}
                x2={boundaryX}
                y1={PAD.top}
                y2={PAD.top + PLOT_H}
                stroke={CHART_INK.axis}
                strokeDasharray="3 3"
              />
              <text x={boundaryX + 6} y={PAD.top + 11} className="tick">
                projected
              </text>
            </g>
          )}

          {gap.length > 0 && (
            <NoDataZone
              x={PAD.left + band * n}
              width={band * gap.length}
              top={PAD.top}
              height={PLOT_H}
              lastMonth={lastMonth}
            />
          )}

          {ticksFor(min, max).map((tick) => (
            <g key={tick}>
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={y(tick)}
                y2={y(tick)}
                stroke={Math.abs(tick) < 1e-9 ? CHART_INK.axis : CHART_INK.grid}
              />
              <text x={PAD.left - 8} y={y(tick) + 3} textAnchor="end" className="tick">
                {compactYen(tick)}
              </text>
            </g>
          ))}

          {runs
            .filter((run) => run.length > 1)
            .map((run) => (
              <polygon
                key={`area-${run[0]}`}
                points={`${centre(run[0] as number)},${zero} ${pointsOf(run, bookedAt)} ${centre(run[run.length - 1] as number)},${zero}`}
                fill="url(#cashAreaME)"
                stroke="none"
              />
            ))}
          {hasProjection && (
            <polygon
              points={`${centre(stretch[0] as number)},${zero} ${stretchPoints(stretch, bookedAt)} ${centre(stretch[stretch.length - 1] as number)},${zero}`}
              fill="url(#cashAreaME)"
              stroke="none"
            />
          )}

          {drawPipeline && (
            <>
              <polygon
                points={`${stretchPoints(pipelineStretch, pipelineAt)} ${stretchPoints([...stretch].reverse(), bookedAt)}`}
                fill="url(#pipelineBandME)"
                stroke="none"
              />
              <polyline
                data-line="pipeline"
                points={stretchPoints(pipelineStretch, pipelineAt)}
                fill="none"
                stroke={INFLOW_RAMP.ar}
                strokeOpacity={0.75}
                strokeWidth={1.25}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </>
          )}

          {runs
            .filter((run) => run.length > 1)
            .map((run) => (
              <polyline
                key={`line-${run[0]}`}
                data-line="actual"
                points={pointsOf(run, bookedAt)}
                fill="none"
                stroke={SERIES.blue}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}
          {stretch.length > 1 && (
            <polyline
              data-line="projected"
              points={stretchPoints(stretch, bookedAt)}
              fill="none"
              stroke={SERIES.blue}
              strokeWidth={2}
              strokeDasharray="5 4"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}

          {points.map((p, index) => {
            if (p.kind === 'missing') {
              const cx = centre(index)
              return (
                <g
                  key={`miss-${p.month}`}
                  data-missing-x={cx}
                  stroke={CHART_INK.tick}
                  strokeWidth={1.75}
                  strokeLinecap="round"
                >
                  <line x1={cx - 4} y1={baselineY - 4} x2={cx + 4} y2={baselineY + 4} />
                  <line x1={cx - 4} y1={baselineY + 4} x2={cx + 4} y2={baselineY - 4} />
                </g>
              )
            }
            if (p.booked === null) return null
            const projectedPoint = p.kind === 'projected'
            return (
              <circle
                key={`pt-${p.month}`}
                cx={centre(index)}
                cy={y(p.booked)}
                r={hover === index ? 5 : 3}
                fill={projectedPoint ? SURFACE : SERIES.blue}
                stroke={projectedPoint ? SERIES.blue : SURFACE}
                strokeWidth={2}
              />
            )
          })}

          {drawLive && liveValue !== null && (
            <rect
              data-live-month={liveBalance?.month}
              x={centre(liveIdx) - 4}
              y={y(liveValue) - 4}
              width={8}
              height={8}
              transform={`rotate(45 ${centre(liveIdx)} ${y(liveValue)})`}
              fill={SERIES.yellow}
              stroke={SURFACE}
              strokeWidth={1.5}
            />
          )}

          {[...points.map((p) => p.month), ...gap].map((month, index) => (
            <text
              key={`x-${month}`}
              x={centre(index)}
              y={LABEL_Y}
              className="tick"
              textAnchor="end"
              transform={`rotate(-35 ${centre(index)} ${LABEL_Y})`}
            >
              {monthLabel(month)}
            </text>
          ))}

          {points.map((p, index) => (
            <rect
              key={`hit-${p.month}`}
              x={PAD.left + band * index}
              y={PAD.top}
              width={band}
              height={PLOT_H}
              fill="transparent"
              onMouseEnter={() => setHover(index)}
            />
          ))}
          {gap.map((month, offset) => (
            <rect
              key={`hit-${month}`}
              x={PAD.left + band * (n + offset)}
              y={PAD.top}
              width={band}
              height={PLOT_H}
              fill="transparent"
              onMouseEnter={() => setHover(n + offset)}
            />
          ))}

          {hover !== null && (
            <line
              x1={centre(hover)}
              x2={centre(hover)}
              y1={PAD.top}
              y2={PAD.top + PLOT_H}
              stroke={CHART_INK.axis}
              strokeWidth={1}
              pointerEvents="none"
            />
          )}
        </svg>

        {hover !== null && hoveredGap !== null && (
          <div className="tooltip" style={{ left: `${(centre(hover) / W) * 100}%` }}>
            <strong>{monthLabel(hoveredGap)}</strong>
            <span className="tooltip-note">No data for this month</span>
          </div>
        )}

        {hover !== null && active !== null && activeMonth !== null && (
          <div className="tooltip" style={{ left: `${(centre(hover) / W) * 100}%` }}>
            <strong>{monthLabel(active.month)}</strong>
            {valueFor(activeMonth, active) !== null && (
              <span>
                {active.kind === 'actual' ? 'Month-end balance' : 'Cash on hand'}{' '}
                <b>{yen(valueFor(activeMonth, active) ?? '0')}</b>
              </span>
            )}
            {active.kind === 'projected' && drawPipeline && activeMonth.projectedBalance !== null && (
              <span>
                With pipeline <b>{yen(activeMonth.projectedBalance)}</b>
              </span>
            )}
            <span className="tooltip-note">{pointNote(active, projectionAnchor)}</span>
            {drawLive && liveBalance && active.month === liveBalance.month && (
              <>
                <span>
                  Live balance <b>{yen(liveBalance.balance)}</b>
                </span>
                <span className="tooltip-note">{LIVE_NOTE}</span>
              </>
            )}
          </div>
        )}
      </div>
    </CashPositionFrame>
  )
}
